import React, { useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import HighlightIcon from '@mui/icons-material/Highlight';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import equal from 'fast-deep-equal';

import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IAnnotationLandmark,
	IAnnotationListProps,
	ISaveAnnotationPromiseResult,
} from 'src/features/annotation/domain/types';
import { IAnnotationData } from 'src/features/annotation/service/FabricManager';
import {
	createAnnotationLandmark,
	deleteAnnotationLandmark,
	updateAnnotationLandmark,
} from 'src/features/annotation/store/annotationLandmarkThunks';
import { AppDispatch } from 'src/store';

import { hasValidationError } from './AnnotationPanel';

const AnnotationList = ({
	currentAnnotation,
	annotations,
	savedAnnotations,
	setAnnotations,
	setSavedAnnotations,
	fabricManager,
	disabled,
	isLoading,
	onChange,
	onSubmit,
	shouldSave,
}: IAnnotationListProps) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const hasUnsavedChanges = useMemo(() => {
		if (!fabricManager) return false;

		const savedMap = new Map(
			savedAnnotations.map((a) => [a.meta_data.id, a]),
		);

		for (const annotation of annotations) {
			const data = fabricManager.getLatestDataForAnnotation(
				annotation.id,
			);
			const saved = savedMap.get(annotation.id);

			if (!saved && data && currentAnnotation) {
				return true;
			}

			if (!equal(saved?.meta_data, data)) {
				return true;
			}
		}

		return false;
	}, [annotations, savedAnnotations, fabricManager, currentAnnotation]);

	const handleLabelChange = (id: string, label: string) => {
		fabricManager?.updateAnnotationLabel(id, label);
		setAnnotations((prev) =>
			prev.map((a) => (a.id === id ? { ...a, label } : a)),
		);
	};

	const handleSaveSuccessAndAutoLock = (
		lockedData: IAnnotationData,
		response: IAnnotationLandmark,
		type: 'create' | 'update',
	) => {
		if (type === 'update') {
			setSavedAnnotations((prev) =>
				prev.map((a) =>
					a.id === response.id
						? {
								...response,
								meta_data: structuredClone(lockedData),
							}
						: a,
				),
			);
		} else {
			setSavedAnnotations((prev) => [
				...prev,
				{ ...response, meta_data: structuredClone(lockedData) },
			]);
		}

		setSavedAnnotations((prev) =>
			prev.map((a) =>
				a.id === response.id
					? {
							...a,
							tool: response.tool,
							label: response.label,
							meta_data: structuredClone(lockedData),
						}
					: a,
			),
		);

		fabricManager?.lockAnnotation(lockedData.id);

		setAnnotations((prev) =>
			prev.map((a) =>
				a.id === lockedData.id ? { ...a, locked: true } : a,
			),
		);
	};

	const handleSaveAnnotations = async (): Promise<string> => {
		if (!siteSlug || !fabricManager) return 'Annotations failed to save';

		const savePromises = annotations.map((annotation) => {
			const data = fabricManager.getLatestDataForAnnotation(
				annotation.id,
			);

			if (!data) {
				deleteAnnotation(annotation.id);
				return undefined;
			}

			const saved = savedAnnotations.find(
				(a) => a.meta_data.id === annotation.id,
			);

			if (equal(saved?.meta_data, data)) return undefined;

			const lockedData: IAnnotationData = structuredClone(data);
			lockedData.locked = true;

			const requestPayload = {
				tool: lockedData.tool,
				label: lockedData.label,
				meta_data: JSON.stringify(lockedData),
			};

			if (saved) {
				return dispatch(
					updateAnnotationLandmark({
						annotationId: saved.id,
						request: requestPayload,
						siteSlug,
					}),
				)
					.unwrap()
					.then((response) => ({
						type: 'update',
						response,
						lockedData,
					}));
			}

			if (currentAnnotation) {
				return dispatch(
					createAnnotationLandmark({
						request: {
							annotation_id: currentAnnotation.id,
							...requestPayload,
						},
						siteSlug,
					}),
				)
					.unwrap()
					.then((response) => ({
						type: 'create',
						response,
						lockedData,
					}));
			}

			return undefined;
		});

		const activeSavePromises = savePromises.filter(
			(p): p is Promise<ISaveAnnotationPromiseResult> => p !== undefined,
		);

		const results = await Promise.allSettled(activeSavePromises);

		const successful: ISaveAnnotationPromiseResult[] = [];
		const failed: ISaveAnnotationPromiseResult[] = [];

		results.forEach((result) => {
			if (result.status === 'fulfilled') successful.push(result.value);
			else failed.push(result.reason);
		});

		successful.forEach(({ type, response, lockedData }) => {
			handleSaveSuccessAndAutoLock(lockedData, response, type);
		});

		return successful.length
			? `Annotations saved successfully` +
					`${failed.length ? ' but some failed to save.' : '.'}`
			: 'Annotations failed to save.';
	};

	const toggleVisibility = (id: string) => {
		fabricManager?.toggleVisibility(id);
		setAnnotations((prev) =>
			prev.map((a) => (a.id === id ? { ...a, visible: !a.visible } : a)),
		);
	};

	const handleHighlight = (id: string) => {
		fabricManager?.highlightAnnotation(id);
	};

	const toggleLock = (id: string) => {
		if (!fabricManager) return;
		const annotationToToggle = annotations.find((a) => a.id === id);
		if (!annotationToToggle) return;

		if (annotationToToggle.locked) {
			annotations.forEach((a) => {
				if (a.id !== id && !a.locked) fabricManager.toggleLock(a.id);
			});
			fabricManager.toggleLock(id);
			setAnnotations((prev) =>
				prev.map((a) =>
					a.id === id
						? { ...a, locked: false }
						: { ...a, locked: true },
				),
			);
		} else {
			fabricManager.toggleLock(id);
			setAnnotations((prev) =>
				prev.map((a) => (a.id === id ? { ...a, locked: true } : a)),
			);
		}
	};

	const deleteAnnotation = async (id: string) => {
		const savedAnnotationToDelete = savedAnnotations.find(
			(a) => a.meta_data.id === id,
		);

		let snackbarShown = false;

		if (savedAnnotationToDelete) {
			dispatch(
				deleteAnnotationLandmark({
					siteSlug: siteSlug!,
					annotationId: savedAnnotationToDelete.id,
					onSuccess: () => {
						snackbar('Annotation deleted successfully', 'success');
						snackbarShown = true;
					},
				}),
			);
		}

		fabricManager?.deleteAnnotation(id);
		setAnnotations((prev) => prev.filter((a) => a.id !== id));

		if (!snackbarShown)
			snackbar('Annotation removed successfully', 'success');
	};

	useEffect(() => {
		onChange?.(hasUnsavedChanges);
	}, [hasUnsavedChanges]);

	useEffect(() => {
		if (!shouldSave.shouldSave || !hasUnsavedChanges || hasValidationError)
			return;
		onSubmit(handleSaveAnnotations());
	}, [shouldSave]);

	return (
		<Box
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				flexShrink: 1,
			}}
		>
			{annotations.length > 0 ? (
				annotations.map((a) => (
					<Box
						key={a.id}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							p: 1.5,
							mb: 1.5,
							border: '1px solid #ccc',
							borderRadius: '8px',
							backgroundColor: '#f9f9f9',
							flexShrink: 0,
						}}
					>
						<TextField
							variant='outlined'
							disabled={disabled || isLoading}
							size='small'
							fullWidth
							multiline
							minRows={1}
							maxRows={10}
							value={a.label}
							onChange={(e) =>
								handleLabelChange(a.id, e.target.value)
							}
							placeholder='Enter the Label'
						/>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-start',
								gap: 0.5,
							}}
						>
							<IconButton
								size='small'
								onClick={() => handleHighlight(a.id)}
							>
								<HighlightIcon />
							</IconButton>
							<IconButton
								size='small'
								disabled={isLoading}
								onClick={() => toggleVisibility(a.id)}
							>
								{a.visible ? (
									<VisibilityIcon />
								) : (
									<VisibilityOffIcon />
								)}
							</IconButton>
							<IconButton
								size='small'
								onClick={() => toggleLock(a.id)}
								disabled={disabled || isLoading}
							>
								{a.locked ? <LockIcon /> : <LockOpenIcon />}
							</IconButton>
							<IconButton
								size='small'
								onClick={() => deleteAnnotation(a.id)}
								disabled={disabled || isLoading}
							>
								<DeleteIcon />
							</IconButton>
						</Box>
					</Box>
				))
			) : (
				<Typography
					variant='body2'
					color='GrayText'
					sx={{ mt: 10, display: 'block' }}
				>
					No annotation available
				</Typography>
			)}
		</Box>
	);
};

export default AnnotationList;
